import { z } from "zod";

export const nativeInvoiceFieldSettingSchema = z.object({
    label: z.string().max(100),
    isIncludedOnPdf: z.boolean(),
});

export const nativeInvoiceFieldSettingsGroupSchema = z.record(
    z.string().min(1).max(50),
    nativeInvoiceFieldSettingSchema,
);

// Tri-state on the render snapshot, matching labelOverrides: omitted leaves
// any saved visibility untouched, explicit null clears it, a record replaces
// the saved set wholesale.
export const nativeInvoicePdfFieldSettingsSchema = z.object({
    // Top-left document heading ("INVOICE" by default). Optional + defaulted
    // to {} like itemsTable below, for the same backward-compat reason: a
    // snapshot persisted before this group existed still parses (the group
    // is absent from its stored jsonb) and falls back to included/no-label.
    header: nativeInvoiceFieldSettingsGroupSchema.optional().default({}),
    from: nativeInvoiceFieldSettingsGroupSchema,
    to: nativeInvoiceFieldSettingsGroupSchema,
    // "Payment" section (PRO-12178): invoiceNumber/poNumber/termsType/
    // invoiceDate/dueDate. Named `details` for backward compatibility - it
    // predates the Payment/Notes split and originally held the memo/terms
    // fields now on `notes` below (see normalizeNativeInvoicePdfFieldSettings).
    details: nativeInvoiceFieldSettingsGroupSchema,
    // "Notes" section (PRO-12178): memo/terms (the free-text "Memo" and
    // "Payment instructions" fields). Split out of `details` into its own
    // group/table so Payment and Notes render as separate accordion
    // sections in the Fields & visibility panel. Optional + defaulted to {}
    // like itemsTable/totals below, for the same backward-compat reason: a
    // snapshot persisted before this split existed still parses (memo/terms
    // settings live in the legacy `details` group instead - see
    // normalizeNativeInvoicePdfFieldSettings).
    notes: nativeInvoiceFieldSettingsGroupSchema.optional().default({}),
    // Items table columns (item/quantity/rate/tax/discount/deposit/amount).
    // Named `itemsTable` (not `items`) to avoid colliding with the unrelated
    // per-line Tax/Discount/Deposit toggle section that already uses the
    // `items` key in InvoiceFieldSettingsOpenSection. Defaulted to an empty
    // record (rather than left as a bare optional) so the inferred type
    // stays non-optional for every consumer that builds/reads a
    // NativeInvoicePdfFieldSettings object directly; a snapshot persisted
    // before this group existed still parses (the group is absent from its
    // stored jsonb), and every field then falls back to included/no-label
    // via isNativeInvoicePdfFieldIncluded's `?? true` default regardless of
    // whether the group is `{}` or missing entirely.
    itemsTable: nativeInvoiceFieldSettingsGroupSchema.optional().default({}),
    // Totals rows beneath the items table (subtotal/tax/discount/deposit/
    // totalDue). Snapshots saved before this group existed kept these rows in
    // `itemsTable`; see normalizeNativeInvoicePdfFieldSettings.
    totals: nativeInvoiceFieldSettingsGroupSchema.optional().default({}),
});

export type NativeInvoiceFieldSetting = z.infer<
    typeof nativeInvoiceFieldSettingSchema
>;
export type NativeInvoiceFieldSettings = z.infer<
    typeof nativeInvoiceFieldSettingsGroupSchema
>;
export type NativeInvoicePdfFieldSettings = z.infer<
    typeof nativeInvoicePdfFieldSettingsSchema
>;
